import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const TABS = ["Reservas", "Calendario", "Bloqueos", "Vehículos", "Categorías", "Ubicaciones", "Menú Superior", "Pilla3", "Cupones", "Noticias", "FAQs", "Opiniones", "Leads venta", "Gestión de Usuarios", "Configuración"];
const ADMIN_TOKEN_KEY = "pc_admin_token";
const AGENDA_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#6366f1", "#a855f7", "#ec4899", "#78716c"];

function getAuthHeaders() {
  const t = sessionStorage.getItem(ADMIN_TOKEN_KEY);
  return t ? { "x-admin-token": t } : {};
}

// Fix legacy relative file URLs (e.g. "/api/files/...") stored before the backend
// started returning absolute URLs. Absolute URLs (http/https/data:) pass through unchanged.
function resolveFileUrl(url) {
  if (!url) return url;
  if (/^(https?:)?\/\//.test(url) || url.startsWith("data:")) return url;
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const full = `${backend}${url.startsWith("/") ? "" : "/"}${url}`;
  const adminToken = sessionStorage.getItem(ADMIN_TOKEN_KEY);
  if (!adminToken) return full;
  return `${full}${full.includes("?") ? "&" : "?"}admin_token=${encodeURIComponent(adminToken)}`;
}

// Null-safe price formatter: rejects null, undefined, empty string and NaN.
function fmtPrecio(p, suffix = " €") {
  if (p === null || p === undefined || p === "") return "—";
  const n = Number(p);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)}${suffix}`;
}

// Null-safe fixed-decimal formatter for lat/lng and similar numeric fields
function fmtNum(n, decimals = 4) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toFixed(decimals);
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(ADMIN_TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("Reservas");

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post("/api/admin/login", { email, password });
      sessionStorage.setItem(ADMIN_TOKEN_KEY, r.data.token);
      setAuthed(true);
      toast.success(`Sesión admin iniciada - ${r.data.name || r.data.email}`);
    } catch {
      toast.error("Credenciales incorrectas");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthed(false);
  };

  if (!authed) {
    return (
      <section className="section-light py-16 min-h-[60vh] flex items-center" data-testid="admin-login">
        <div className="max-w-md mx-auto px-4 sm:px-6 w-full">
          <div className="divider-red mb-3"></div>
          <h1 className="font-display text-3xl mb-6">Acceso Administración</h1>
          <form onSubmit={login} className="space-y-3 bg-black text-white p-6 rounded-lg">
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-400">Email de Administrador</label>
              <input type="email" className="pc-input text-black" value={email} onChange={e => setEmail(e.target.value)} required data-testid="admin-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-400">Contraseña</label>
              <input type="password" className="pc-input text-black" value={password} onChange={e => setPassword(e.target.value)} required data-testid="admin-password" />
            </div>
            <button type="submit" className="btn-red w-full py-3" data-testid="admin-login-btn">Entrar</button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="section-light py-10" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="divider-red mb-3"></div>
            <h1 className="font-display text-3xl sm:text-4xl">Panel de Administración</h1>
          </div>
          <button onClick={logout} className="text-xs px-3 py-2 bg-neutral-200 rounded" data-testid="admin-logout">Cerrar sesión</button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 border-b border-neutral-200">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 text-sm uppercase tracking-wider font-semibold ${tab === t ? "border-b-2 border-[var(--pc-red)] text-[var(--pc-red)]" : "text-neutral-600"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Reservas" && <ReservationsTab />}
        {tab === "Calendario" && <CalendarioTab />}
        {tab === "Bloqueos" && <BloqueosTab />}
        {tab === "Vehículos" && <VehiclesTab />}
        {tab === "Categorías" && <CategoriasTab />}
        {tab === "Ubicaciones" && <UbicacionesTab />}
        {tab === "Menú Superior" && <NavConfigTab />}
        {tab === "Pilla3" && <Pilla3Tab />}
        {tab === "Cupones" && <CouponsTab />}
        {tab === "Configuración" && <ConfigTab />}
        {tab === "Noticias" && <CrudTab name="news" label="Noticia" fields={[["titulo", "Título"], ["imagen_url", "Imagen URL"], ["contenido", "Contenido"]]} />}
        {tab === "FAQs" && <CrudTab name="faqs" label="FAQ" fields={[["categoria", "Categoría"], ["pregunta", "Pregunta"], ["respuesta", "Respuesta"]]} />}
        {tab === "Opiniones" && <CrudTab name="testimonials" label="Opinión" fields={[["fecha", "Fecha"], ["nombre", "Nombre"], ["texto", "Texto"], ["estrellas", "Estrellas (1-5)"]]} numericFields={["estrellas"]} />}
        {tab === "Leads venta" && <LeadsTab />}
        {tab === "Gestión de Usuarios" && <UsuariosTab />}
      </div>
    </section>
  );
}

function ReservationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceRid, setInvoiceRid] = useState(null);
  const [invoiceFilter, setInvoiceFilter] = useState("all"); // all | none | draft | sent | failed
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const load = () => {
    setLoading(true);
    return axios.get("/api/reservations", { headers: getAuthHeaders() })
      .then(r => setItems(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const releaseDeposit = async (intentId) => {
    if (!window.confirm("¿Liberar la fianza retenida? Esto cancelará la retención en Stripe.")) return;
    try {
      await axios.post(`/api/checkout/deposit/${intentId}/release`, {}, { headers: getAuthHeaders() });
      toast.success("Fianza liberada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al liberar la fianza");
    }
  };
  const cancelReservation = async (rid) => {
    if (!window.confirm("¿Anular esta reserva? Se avisará al cliente por email.")) return;
    try {
      await axios.post(`/api/reservations/${rid}/cancel`, {}, { headers: getAuthHeaders() });
      toast.success("Reserva anulada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al anular la reserva");
    }
  };
  const ampliarReserva = async (r) => {
    const actual = (r.fecha_devolucion || "").slice(0, 16);
    const nueva = window.prompt(`Nueva fecha y hora de devolución (AAAA-MM-DDTHH:MM). Actual: ${actual}`, actual);
    if (!nueva || nueva === actual) return;
    try {
      const { data } = await axios.post(`/api/reservations/${r.id}/ampliar`, {
        nueva_fecha_devolucion: nueva, origin_url: window.location.origin,
      }, { headers: getAuthHeaders() });
      if (data.metodo === "tarjeta_guardada") {
        toast.success(`Ampliación cobrada: ${data.importe_cobrado.toFixed(2)} € a la tarjeta guardada del cliente`);
      } else if (data.url) {
        try { await navigator.clipboard.writeText(data.url); } catch (err) { /* ignore */ }
        window.prompt("El cliente no tiene tarjeta guardada. Copia y envíale este link de pago:", data.url);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo ampliar la reserva");
    }
  };
  const createDeposit = async (reservationId) => {
    try {
      await axios.post(`/api/checkout/deposit`, { reservation_id: reservationId }, { headers: getAuthHeaders() });
      toast.success("Fianza creada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear la fianza");
    }
  };
  const captureDeposit = async (intentId) => {
    const raw = window.prompt("Importe a cobrar en € (deja vacío para cobrar el total retenido):", "");
    if (raw === null) return;
    const trimmed = raw.trim();
    let body = {};
    if (trimmed !== "") {
      const n = parseFloat(trimmed);
      if (!Number.isFinite(n) || n <= 0) { toast.error("Importe inválido"); return; }
      body = { amount: n };
    }
    try {
      await axios.post(`/api/checkout/deposit/${intentId}/capture`, body, { headers: getAuthHeaders() });
      toast.success("Fianza cobrada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cobrar la fianza");
    }
  };

  const inDateRange = (r) => {
    if (!dateFrom && !dateTo) return true;
    const pickup = (r.fecha_recogida || "").slice(0, 10);
    if (!pickup) return false;
    if (dateFrom && pickup < dateFrom) return false;
    if (dateTo && pickup > dateTo) return false;
    return true;
  };
  const inStatusFilter = (r) => {
    if (invoiceFilter === "all") return true;
    if (invoiceFilter === "none") return !r.invoice;
    return r.invoice?.status === invoiceFilter;
  };

  const dateFiltered = items.filter(inDateRange);
  const filtered = dateFiltered.filter(inStatusFilter);

  // Counters reflect date-range filter to keep them coherent
  const counts = {
    all: dateFiltered.length,
    none: dateFiltered.filter(r => !r.invoice).length,
    draft: dateFiltered.filter(r => r.invoice?.status === "draft").length,
    sent: dateFiltered.filter(r => r.invoice?.status === "sent").length,
    failed: dateFiltered.filter(r => r.invoice?.status === "failed").length,
  };

  const applyPreset = (preset) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "clear") { setDateFrom(""); setDateTo(""); return; }
    if (preset === "year") {
      setDateFrom(`${y}-01-01`);
      setDateTo(`${y}-12-31`);
      return;
    }
    // Quarters: Q1 Jan-Mar, Q2 Apr-Jun, Q3 Jul-Sep, Q4 Oct-Dec
    const q = Math.floor(m / 3); // 0..3 current
    let targetQ = q;
    let targetY = y;
    if (preset === "prev-quarter") {
      targetQ = q - 1;
      if (targetQ < 0) { targetQ = 3; targetY = y - 1; }
    }
    const startMonth = targetQ * 3;
    const start = new Date(targetY, startMonth, 1);
    const end = new Date(targetY, startMonth + 3, 0); // last day of quarter
    setDateFrom(iso(start));
    setDateTo(iso(end));
  };

  const pill = (key, label, cls) => (
    <button
      key={key}
      onClick={() => setInvoiceFilter(key)}
      className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider transition ${invoiceFilter === key ? "bg-[var(--pc-red)] text-white shadow" : cls}`}
      data-testid={`invoice-filter-${key}`}
    >
      {label} <span className="opacity-70">({counts[key]})</span>
    </button>
  );

  const exportCSV = () => {
    const IVA = 0.21;
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      "Nº Factura", "Fecha Factura", "Estado factura",
      "ID Reserva", "Estado Reserva",
      "Cliente", "NIF", "Email", "Teléfono",
      "Coche", "Fecha recogida", "Fecha devolución",
      "Base imponible €", "IVA 21% €", "Total €",
    ];
    const rows = [];
    let sumBase = 0, sumIva = 0, sumTotal = 0;
    filtered.forEach(r => {
      const total = Number(r.total) || 0;
      const base = +(total / (1 + IVA)).toFixed(2);
      const ivaAmount = +(total - base).toFixed(2);
      sumBase += base;
      sumIva += ivaAmount;
      sumTotal += total;
      const inv = r.invoice || {};
      rows.push([
        inv.invoice_number || "",
        inv.invoice_date || "",
        inv.status || "sin factura",
        r.id,
        r.status,
        `${r.customer?.nombre || ""} ${r.customer?.apellidos || ""}`.trim(),
        r.customer?.dni || "",
        r.customer?.email || "",
        r.customer?.telefono || "",
        r.vehicle_name || "",
        r.fecha_recogida || "",
        r.fecha_devolucion || "",
        base.toFixed(2).replace(".", ","),
        ivaAmount.toFixed(2).replace(".", ","),
        total.toFixed(2).replace(".", ","),
      ].map(esc).join(";"));
    });
    // Totals row
    if (filtered.length > 0) {
      rows.push(""); // blank separator
      rows.push([
        "", "", "", "", "", "", "", "", "", "", "",
        "TOTALES",
        sumBase.toFixed(2).replace(".", ","),
        sumIva.toFixed(2).replace(".", ","),
        sumTotal.toFixed(2).replace(".", ","),
      ].map(esc).join(";"));
    }
    const csv = "\uFEFF" + [header.map(esc).join(";"), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const rangeTag = dateFrom || dateTo ? `_${dateFrom || "inicio"}_a_${dateTo || "hoy"}` : "";
    const ts = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `facturas_${invoiceFilter}${rangeTag}_${ts}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exportadas ${filtered.length} filas`);
  };

  return (
    <div className="overflow-auto">
      {/* Date range filter */}
      <div className="flex flex-wrap gap-2 mb-3 items-end bg-neutral-50 border border-neutral-200 rounded-lg p-3" data-testid="date-filter-bar">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Desde (fecha recogida)</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="pc-input py-1 text-sm" data-testid="date-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Hasta</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="pc-input py-1 text-sm" data-testid="date-to" />
        </div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => applyPreset("quarter")} className="text-xs px-3 py-2 rounded-full bg-neutral-200 hover:bg-neutral-300" data-testid="preset-quarter">Este trimestre</button>
          <button onClick={() => applyPreset("prev-quarter")} className="text-xs px-3 py-2 rounded-full bg-neutral-200 hover:bg-neutral-300" data-testid="preset-prev-quarter">Trimestre anterior</button>
          <button onClick={() => applyPreset("year")} className="text-xs px-3 py-2 rounded-full bg-neutral-200 hover:bg-neutral-300" data-testid="preset-year">Este año</button>
          {(dateFrom || dateTo) && (
            <button onClick={() => applyPreset("clear")} className="text-xs px-3 py-2 rounded-full bg-[var(--pc-red)] text-white" data-testid="preset-clear">Quitar fechas ✕</button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="invoice-filter-bar">
        {pill("all", "Todas", "bg-neutral-100 text-neutral-700 hover:bg-neutral-200")}
        {pill("none", "Sin factura", "bg-neutral-100 text-neutral-500 hover:bg-neutral-200")}
        {pill("draft", "Borrador", "bg-yellow-50 text-yellow-700 hover:bg-yellow-100")}
        {pill("sent", "Enviadas", "bg-green-50 text-green-700 hover:bg-green-100")}
        {pill("failed", "Error", "bg-red-50 text-red-700 hover:bg-red-100")}
        <button
          onClick={exportCSV}
          disabled={filtered.length === 0}
          className="ml-auto px-3 py-1 rounded-full text-xs uppercase tracking-wider bg-black text-white hover:bg-[var(--pc-red)] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="export-csv-btn"
        >
          Exportar CSV ({filtered.length})
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-neutral-100"><tr>
          <th className="px-3 py-2 text-left">ID</th><th className="px-3 py-2 text-left">Coche</th>
          <th className="px-3 py-2 text-left">Cliente</th><th className="px-3 py-2 text-left">DNI</th>
          <th className="px-3 py-2 text-left">Fechas</th><th className="px-3 py-2 text-left">Total</th>
          <th className="px-3 py-2 text-left">Estado</th>
          <th className="px-3 py-2 text-left">Factura</th>
          <th className="px-3 py-2 text-left">Estado factura</th>
          <th className="px-3 py-2 text-left">Fianza</th>
          <th className="px-3 py-2 text-left">Docs</th>
        </tr></thead>
        <tbody>
          {filtered.map(r => (
            <tr key={r.id} className="border-t border-neutral-200" data-testid={`reservation-row-${r.id}`}>
              <td className="px-3 py-2 text-xs">{r.id.slice(0, 8)}</td>
              <td className="px-3 py-2">{r.vehicle_name}</td>
              <td className="px-3 py-2">{r.customer.nombre} {r.customer.apellidos}</td>
              <td className="px-3 py-2">{r.customer.dni}</td>
              <td className="px-3 py-2 text-xs">{r.fecha_recogida?.slice(0, 10)} → {r.fecha_devolucion?.slice(0, 10)}</td>
              <td className="px-3 py-2 font-semibold">{r.total.toFixed(2)} €</td>
              <td className="px-3 py-2">
                <span className={`text-xs px-2 py-1 rounded ${r.status === "paid" ? "bg-green-100 text-green-700" : r.status === "cancelled" ? "bg-neutral-200 text-neutral-600" : "bg-yellow-100 text-yellow-700"}`}>{r.status}</span>
                {r.status !== "paid" && r.status !== "cancelled" && (
                  <button onClick={() => cancelReservation(r.id)} className="block mt-1 text-[11px] px-2 py-0.5 bg-red-50 text-red-600 hover:bg-red-100 rounded" data-testid={`cancel-reservation-${r.id}`}>Anular</button>
                )}
                {r.status === "paid" && (
                  <button onClick={() => ampliarReserva(r)} className="block mt-1 text-[11px] px-2 py-0.5 bg-neutral-100 text-neutral-700 hover:bg-neutral-200 rounded" data-testid={`ampliar-reserva-${r.id}`}>Ampliar</button>
                )}
              </td>
              <td className="px-3 py-2">
                <button
                  onClick={() => setInvoiceRid(r.id)}
                  disabled={r.status === "cancelled"}
                  className="text-xs px-3 py-1 bg-black text-white rounded hover:bg-[var(--pc-red)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-black"
                  data-testid={`invoice-btn-${r.id}`}
                >Gestionar Factura</button>
              </td>
              <td className="px-3 py-2 text-xs" data-testid={`invoice-status-${r.id}`}>
                {!r.invoice && <span className="text-neutral-400">—</span>}
                {r.invoice?.status === "draft" && <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-700">Borrador</span>}
                {r.invoice?.status === "sent" && (
                  <span className="px-2 py-1 rounded bg-green-100 text-green-700" title={r.invoice.sent_at || ""}>
                    Enviada · {r.invoice.invoice_number}
                    {r.invoice.invoice_date && <span className="block text-[10px] text-green-600">{r.invoice.invoice_date}</span>}
                  </span>
                )}
                {r.invoice?.status === "failed" && <span className="px-2 py-1 rounded bg-red-100 text-red-700">Error</span>}
              </td>
              <td className="px-3 py-2 text-xs" data-testid={`deposit-cell-${r.id}`}>
                {r.deposit_intent_id ? (
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] text-neutral-500">{r.deposit_status || "authorized"}{(r.deposit_status === "captured" && r.deposit_captured_amount != null) ? ` · ${Number(r.deposit_captured_amount).toFixed(2)} €` : (r.deposit_amount ? ` · ${Number(r.deposit_amount).toFixed(2)} €` : "")}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => releaseDeposit(r.deposit_intent_id)}
                        disabled={["captured", "released"].includes(r.deposit_status) || r.status === "cancelled"}
                        className="px-2 py-1 bg-neutral-200 hover:bg-neutral-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-neutral-200 rounded text-[11px]"
                        data-testid={`deposit-release-${r.id}`}
                      >Liberar fianza</button>
                      <button
                        onClick={() => captureDeposit(r.deposit_intent_id)}
                        disabled={["captured", "released"].includes(r.deposit_status) || r.status === "cancelled"}
                        className="px-2 py-1 bg-[var(--pc-red)] text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[var(--pc-red)] rounded text-[11px]"
                        data-testid={`deposit-capture-${r.id}`}
                      >Cobrar fianza</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => createDeposit(r.id)}
                    disabled={r.status === "cancelled"}
                    className="px-2 py-1 bg-neutral-800 text-white hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-neutral-800 rounded text-[11px]"
                    data-testid={`deposit-create-${r.id}`}
                  >Crear fianza</button>
                )}
              </td>
              <td className="px-3 py-2 text-xs">
                {r.documento_verificado_status === "Declined" && (
                  <div className="mb-1 px-2 py-1 bg-red-100 text-red-700 rounded text-[11px] font-medium" data-testid={`didit-declined-${r.id}`}>
                    ⚠ Documento no verificado (Didit: Declined) — revisar manualmente
                  </div>
                )}
                {["dni_frontal", "dni_trasero", "carnet_frontal", "carnet_trasero"].map(k => r.customer[k] && (
                  <a key={k} href={resolveFileUrl(r.customer[k])} target="_blank" rel="noreferrer" className="text-blue-600 underline mr-1">{k.replace("_", " ")}</a>
                ))}
                {r.customer.conductor2 && (
                  <div className="mt-1 pt-1 border-t border-neutral-200">
                    <div className="text-[10px] text-neutral-500 mb-0.5">2º conductor:</div>
                    {["dni_frontal", "dni_trasero", "carnet_frontal", "carnet_trasero"].map(k => r.customer.conductor2[k] && (
                      <a key={`c2-${k}`} href={resolveFileUrl(r.customer.conductor2[k])} target="_blank" rel="noreferrer" className="text-blue-600 underline mr-1" data-testid={`c2-doc-${k}-${r.id}`}>{k.replace("_", " ")}</a>
                    ))}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="p-4 text-neutral-500" data-testid="reservations-empty">
          {loading ? "Cargando reservas..." : (items.length === 0 ? "Aún no hay reservas." : "Ninguna reserva coincide con el filtro.")}
        </div>
      )}
      {invoiceRid && <InvoiceModal rid={invoiceRid} onClose={() => setInvoiceRid(null)} onSent={load} />}
    </div>
  );
}

function InvoiceModal({ rid, onClose, onSent }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("draft");
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewNonce, setPreviewNonce] = useState(Date.now());
  const IVA = 0.21;

  const load = async () => {
    try {
      const { data } = await axios.get(`/api/reservations/${rid}/invoice`, { headers: getAuthHeaders() });
      setItems(data.items || []);
      setStatus(data.status || "draft");
      setInvoiceNumber(data.invoice_number);
      setInvoiceDate(data.invoice_date);
    } catch (e) {
      toast.error("Error cargando factura");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid]);

  const updateItem = (i, key, value) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: key === "precio_base" ? (value === "" ? "" : Number(value)) : value };
    setItems(next);
  };
  const addItem = () => setItems([...items, { descripcion: "", coche: "", precio_base: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, j) => j !== i));

  const totals = items.reduce((acc, it) => {
    const base = Number(it.precio_base) || 0;
    const iva = +(base * IVA).toFixed(2);
    acc.base += base;
    acc.iva += iva;
    return acc;
  }, { base: 0, iva: 0 });
  totals.total = +(totals.base + totals.iva).toFixed(2);

  const saveDraft = async () => {
    setLoading(true);
    try {
      await axios.put(`/api/reservations/${rid}/invoice`, { items }, { headers: getAuthHeaders() });
      setPreviewNonce(Date.now());
      toast.success("Borrador guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally { setLoading(false); }
  };

  const sendNow = async () => {
    if (status === "sent") return;
    if (!window.confirm("¿Enviar factura ahora al cliente por email?")) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`/api/reservations/${rid}/invoice/send`, { items }, { headers: getAuthHeaders() });
      setStatus("sent");
      setInvoiceNumber(data.invoice_number);
      setInvoiceDate(data.invoice_date);
      setPreviewNonce(Date.now());
      toast.success(`Factura ${data.invoice_number} enviada`);
      onSent && onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar");
    } finally { setLoading(false); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const adminTok = sessionStorage.getItem(ADMIN_TOKEN_KEY) || "";
  const pdfUrl = `${backend}/api/reservations/${rid}/invoice.pdf?token=${encodeURIComponent(adminTok)}&_=${previewNonce}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="invoice-modal">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between bg-white sticky top-0 z-10">
          <div>
            <h3 className="font-display text-2xl">Gestionar Factura</h3>
            {invoiceNumber && <div className="text-xs text-neutral-500 mt-1">Nº {invoiceNumber} · {invoiceDate}</div>}
            {status === "sent" && <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded bg-green-100 text-green-700" data-testid="invoice-status-sent">Enviada</span>}
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-black" data-testid="invoice-close">✕</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 flex-1 overflow-hidden">
          {/* LEFT: editor */}
          <div className="p-5 overflow-y-auto border-r border-neutral-200">
            <h4 className="font-display text-lg mb-3">Conceptos</h4>
            <div className="space-y-2 mb-3">
              {items.map((it, i) => {
                const base = Number(it.precio_base) || 0;
                const iva = +(base * IVA).toFixed(2);
                const total = +(base + iva).toFixed(2);
                return (
                  <div key={i} className="border border-neutral-200 rounded p-3 grid grid-cols-1 gap-2 text-sm" data-testid={`invoice-item-${i}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-neutral-500">Descripción</label>
                        <input disabled={status === "sent"} className="pc-input" value={it.descripcion || ""} onChange={e => updateItem(i, "descripcion", e.target.value)} />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-neutral-500">Coche</label>
                        <input disabled={status === "sent"} className="pc-input" value={it.coche || ""} onChange={e => updateItem(i, "coche", e.target.value)} />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-neutral-500">Precio base</label>
                        <input disabled={status === "sent"} type="number" step="0.01" className="pc-input" value={it.precio_base ?? 0} onChange={e => updateItem(i, "precio_base", e.target.value)} />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-neutral-500">IVA 21%</label>
                        <div className="pc-input bg-neutral-100 text-neutral-700">{iva.toFixed(2)} €</div>
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-neutral-500">Total</label>
                        <div className="pc-input bg-neutral-100 font-semibold">{total.toFixed(2)} €</div>
                      </div>
                      <div className="flex items-end">
                        {status !== "sent" && <button onClick={() => removeItem(i)} className="text-xs px-2 py-2 bg-red-100 text-red-700 rounded w-full" data-testid={`invoice-remove-${i}`}>Eliminar</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {status !== "sent" && (
              <button onClick={addItem} className="text-sm px-3 py-2 bg-neutral-200 rounded mb-4" data-testid="invoice-add-item">+ Añadir Concepto Adicional</button>
            )}

            <div className="border-t border-neutral-200 pt-3 space-y-1 text-sm" data-testid="invoice-totals">
              <div className="flex justify-between"><span className="text-neutral-500">Base imponible</span><span>{totals.base.toFixed(2)} €</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">IMPORTE IVA (21%)</span><span>{totals.iva.toFixed(2)} €</span></div>
              <div className="flex justify-between font-semibold text-lg pt-1 border-t border-neutral-200"><span>TOTAL IVA INCLUDED</span><span className="text-[var(--pc-red)]">{totals.total.toFixed(2)} €</span></div>
            </div>

            <div className="mt-5 flex gap-2 flex-wrap">
              {status !== "sent" && (
                <>
                  <button onClick={saveDraft} disabled={loading} className="px-4 py-2 border border-neutral-300 rounded text-sm" data-testid="invoice-save-draft">Guardar borrador</button>
                  <button onClick={sendNow} disabled={loading} className="btn-red px-5 py-2 text-sm" data-testid="invoice-send-now">Enviar Factura Ahora</button>
                </>
              )}
              <a href={pdfUrl} target="_blank" rel="noreferrer" className="px-4 py-2 border border-neutral-300 rounded text-sm" data-testid="invoice-open-pdf">Abrir PDF</a>
            </div>
          </div>

          {/* RIGHT: PDF preview */}
          <div className="bg-neutral-100 overflow-hidden">
            <iframe title="factura-preview" src={pdfUrl} className="w-full h-full min-h-[500px]" data-testid="invoice-pdf-preview" />
          </div>
        </div>
      </div>
    </div>
  );
}

const VEHICLE_SECTIONS = [
  {
    title: "Datos básicos",
    fields: [
      ["marca_modelo", "Marca/Modelo"],
      ["color", "Color del coche"],
      ["color_agenda", "Color para agenda de alquiler"],
      ["categoria", "Categoría (Económico/SUV/Eléctrico/Premium)"],
      ["precio_dia", "Precio/día (€)"],
      ["precio_hora", "Precio/hora (€)"],
      ["fianza", "Fianza (€)"],
      ["matricula", "Matrícula"],
      ["kilometros_salida", "Kilómetros de salida"],
      ["tipo_disponibilidad", "Disponibilidad (Alquiler/Venta/Ambos)"],
      ["precio_venta", "Precio venta"],
      ["descripcion_coche", "Descripción"],
    ],
  },
  {
    title: "Ficha técnica",
    fields: [
      ["transmision", "Transmisión"],
      ["plazas", "Plazas"],
      ["maletero", "Maletero"],
      ["año", "Año"],
      ["potencia", "Potencia (CV)"],
      ["puertas", "Puertas"],
      ["combustible", "Combustible"],
      ["nivel_combustible", "Nivel de combustible (%)"],
      ["velocidades", "Velocidades"],
      ["etiqueta", "Etiqueta"],
      ["km", "Km"],
    ],
  },
  {
    title: "Multimedia",
    fields: [
      ["imagen_url", "Imagen URL"],
      ["imagenes_galeria_csv", "Galería (URLs separadas por coma)"],
    ],
  },
  {
    title: "Ubicación",
    fields: [
      ["latitud", "Latitud"],
      ["longitud", "Longitud"],
    ],
  },
];

function VehiclesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [uploadingImg, setUploadingImg] = useState({});
  const [cloudinaryImages, setCloudinaryImages] = useState(null); // null = aún no cargada
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [pickerOpenFor, setPickerOpenFor] = useState(null); // "imagen_url" | "imagenes_galeria_csv" | null
  const [vehicleBlocks, setVehicleBlocks] = useState([]);
  const [newBlock, setNewBlock] = useState({ fecha_inicio: "", fecha_fin: "", motivo: "" });
  const [savingBlock, setSavingBlock] = useState(false);
  const load = () => axios.get("/api/vehicles?visible_only=false").then(r => setItems(r.data || []));
  useEffect(() => {
    load();
    axios.get("/api/ubicaciones").then(r => setUbicaciones(r.data || [])).catch(() => { });
  }, []);

  const loadVehicleBlocks = (vehicleId) => {
    if (!vehicleId) { setVehicleBlocks([]); return; }
    axios.get("/api/admin/bloqueos", { params: { vehicle_id: vehicleId }, headers: getAuthHeaders() })
      .then(r => setVehicleBlocks(r.data || [])).catch(() => setVehicleBlocks([]));
  };

  const crearBloqueoVehiculo = async () => {
    if (!newBlock.fecha_inicio || !newBlock.fecha_fin) { toast.error("Indica ambas fechas"); return; }
    setSavingBlock(true);
    try {
      await axios.post("/api/admin/bloqueos", {
        vehicle_id: editing.id,
        fecha_inicio: `${newBlock.fecha_inicio}T00:00`,
        fecha_fin: `${newBlock.fecha_fin}T23:59`,
        motivo: newBlock.motivo,
      }, { headers: getAuthHeaders() });
      toast.success("Días bloqueados");
      setNewBlock({ fecha_inicio: "", fecha_fin: "", motivo: "" });
      loadVehicleBlocks(editing.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el bloqueo");
    } finally {
      setSavingBlock(false);
    }
  };

  const eliminarBloqueoVehiculo = async (id) => {
    if (!window.confirm("¿Eliminar este bloqueo?")) return;
    await axios.delete(`/api/admin/bloqueos/${id}`, { headers: getAuthHeaders() });
    loadVehicleBlocks(editing.id);
  };

  useEffect(() => {
    if (editing?.id) loadVehicleBlocks(editing.id); else setVehicleBlocks([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  const openPicker = async (field) => {
    setPickerOpenFor(pickerOpenFor === field ? null : field);
    if (cloudinaryImages === null && !loadingGallery) {
      setLoadingGallery(true);
      try {
        const { data } = await axios.get("/api/vehicles/cloudinary-images", { headers: getAuthHeaders() });
        setCloudinaryImages(data.images || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error cargando la galería de Cloudinary");
        setCloudinaryImages([]);
      } finally {
        setLoadingGallery(false);
      }
    }
  };

  const pickFromGallery = (field, url) => {
    if (field === "imagen_url") {
      setEditing(prev => ({ ...prev, imagen_url: url }));
    } else if (field === "imagenes_galeria_csv") {
      setEditing(prev => ({ ...prev, imagenes_galeria_csv: [prev.imagenes_galeria_csv, url].filter(Boolean).join(", ") }));
    }
    setPickerOpenFor(null);
  };

  const uploadVehicleImage = async (file, onDone) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post("/api/vehicles/upload-image", fd, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      onDone(data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo la imagen");
    }
  };

  const pickUbicacion = (uid) => {
    const u = ubicaciones.find(x => x.id === uid);
    if (!u) { setEditing({ ...editing, ubicacion_id: "" }); return; }
    setEditing({ ...editing, ubicacion_id: u.id, latitud: u.latitud, longitud: u.longitud });
  };

  const save = async (data) => {
    const payload = { ...data };
    if (payload.imagenes_galeria_csv !== undefined) {
      payload.imagenes_galeria = (payload.imagenes_galeria_csv || "").split(",").map(s => s.trim()).filter(Boolean);
      delete payload.imagenes_galeria_csv;
    }
    // Validate precio_dia before sending
    const rawPrecio = payload.precio_dia;
    if (rawPrecio === undefined || rawPrecio === null || rawPrecio === "") {
      toast.error("El precio por día es obligatorio");
      return;
    }
    const precioNum = parseFloat(rawPrecio);
    if (!Number.isFinite(precioNum) || precioNum < 0) {
      toast.error("El precio por día debe ser un número válido y positivo");
      return;
    }
    payload.precio_dia = precioNum;
    ["latitud", "longitud", "precio_venta", "fianza", "precio_hora"].forEach(k => { if (payload[k] !== undefined && payload[k] !== "") payload[k] = parseFloat(payload[k]); });
    ["plazas", "año", "potencia", "puertas", "velocidades", "km"].forEach(k => { if (payload[k] !== undefined && payload[k] !== "") payload[k] = parseInt(payload[k]); });
    payload.visible = !!payload.visible;
    if (!String(payload.marca_modelo || "").trim()) { toast.error("La marca/modelo es obligatoria"); return; }
    if (!String(payload.categoria || "").trim()) { toast.error("La categoría es obligatoria"); return; }
    // Al crear, no enviar campos vacíos (evita 422 por "" en campos numéricos)
    if (!editing.id) Object.keys(payload).forEach(k => { if (payload[k] === "" || payload[k] === undefined) delete payload[k]; });
    const headers = getAuthHeaders();
    try {
      if (editing.id) await axios.put(`/api/vehicles/${editing.id}`, payload, { headers });
      else await axios.post(`/api/vehicles`, payload, { headers });
      toast.success("Guardado");
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el coche");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar coche?")) return;
    await axios.delete(`/api/vehicles/${id}`, { headers: getAuthHeaders() }); load();
  };

  const toggleVisible = async (v) => {
    await axios.put(`/api/vehicles/${v.id}`, { visible: !v.visible }, { headers: getAuthHeaders() }); load();
  };

  return (
    <div>
      <div className="mb-4">
        <button onClick={() => setEditing({ visible: true, imagenes_galeria_csv: "" })} className="btn-red px-4 py-2 text-sm" data-testid="add-vehicle-btn">+ Añadir coche</button>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-100"><tr>
            <th className="px-3 py-2 text-left">Modelo</th><th className="px-3 py-2 text-left">Matrícula</th><th className="px-3 py-2 text-left">Categoría</th>
            <th className="px-3 py-2 text-left">Precio/día</th><th className="px-3 py-2 text-left">Tipo</th>
            <th className="px-3 py-2 text-left">Visible</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {items.map(v => (
              <tr key={v.id} className="border-t border-neutral-200" data-testid={`vehicle-row-${v.id}`}>
                <td className="px-3 py-2">{v.marca_modelo}</td>
                <td className="px-3 py-2">{v.matricula || "—"}</td>
                <td className="px-3 py-2">{v.categoria}</td>
                <td className="px-3 py-2">{fmtPrecio(v.precio_dia)}</td>
                <td className="px-3 py-2">{v.tipo_disponibilidad}</td>
                <td className="px-3 py-2">
                  <button onClick={() => toggleVisible(v)} className={`text-xs px-2 py-1 rounded ${v.visible ? "bg-green-100 text-green-700" : "bg-neutral-200"}`}>{v.visible ? "Visible" : "Oculto"}</button>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing({ ...v, imagenes_galeria_csv: (v.imagenes_galeria || []).join(", ") })} className="text-xs px-2 py-1 mr-1 bg-neutral-100 rounded" data-testid={`edit-vehicle-${v.id}`}>Editar</button>
                  <button onClick={() => remove(v.id)} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded" data-testid={`delete-vehicle-${v.id}`}>Eliminar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
            {/* Sticky header with live preview */}
            <div className="px-6 py-4 border-b border-neutral-200 bg-white sticky top-0 z-10">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-display text-2xl" data-testid="vehicle-modal-title">{editing.id ? "Editar" : "Añadir"} coche</h3>
                <div className="hidden sm:flex items-center gap-3 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 flex-1 max-w-md" data-testid="vehicle-preview-card">
                  <div className="w-12 h-12 rounded overflow-hidden bg-neutral-200 flex-shrink-0">
                    {editing.imagen_url
                      ? <img src={editing.imagen_url} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                      : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">Sin imagen</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm truncate" data-testid="preview-name">{editing.marca_modelo || "Nuevo coche"}</div>
                    <div className="text-xs uppercase tracking-wider text-neutral-500 truncate" data-testid="preview-category">
                      {editing.categoria || "Sin categoría"} · {editing.tipo_disponibilidad || "Alquiler"}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-display text-lg text-[var(--pc-red)] leading-none" data-testid="preview-price">
                      {fmtPrecio(editing.precio_dia)}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">por día</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="px-6 py-4 overflow-y-auto flex-1" data-testid="vehicle-modal-body">
              <div className="mb-5 p-3 bg-neutral-50 rounded border border-neutral-200">
                <label className="text-xs uppercase tracking-wider text-neutral-500">Asignar ubicación (auto-rellena lat/lng)</label>
                <select
                  className="pc-input"
                  value={editing.ubicacion_id || ""}
                  onChange={e => pickUbicacion(e.target.value)}
                  data-testid="vehicle-ubicacion-select"
                >
                  <option value="">— Coordenadas manuales —</option>
                  {ubicaciones.map(u => (
                    <option key={u.id} value={u.id}>{u.nombre_zona} ({fmtNum(u.latitud, 4)}, {fmtNum(u.longitud, 4)})</option>
                  ))}
                </select>
              </div>

              {VEHICLE_SECTIONS.map(section => (
                <fieldset
                  key={section.title}
                  className="mb-5 border border-neutral-200 rounded-lg p-4"
                  data-testid={`vehicle-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <legend className="px-2 text-xs uppercase tracking-widest font-semibold text-[var(--pc-red)]">
                    {section.title}
                  </legend>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {section.fields.map(([k, label]) => (
                      <div key={k} className={k === "descripcion_coche" || k === "imagenes_galeria_csv" ? "col-span-2" : ""}>
                        <label className="text-xs uppercase tracking-wider text-neutral-500">{label}</label>
                        {k === "descripcion_coche" ? (
                          <textarea className="pc-input" rows="3" value={editing[k] || ""} onChange={e => setEditing({ ...editing, [k]: e.target.value })} />
                        ) : k === "imagen_url" ? (
                          <div>
                            <div className="flex gap-2 items-start">
                              <input className="pc-input flex-1" value={editing[k] ?? ""} onChange={e => setEditing({ ...editing, [k]: e.target.value })} placeholder="URL o sube un archivo →" />
                              <label className="btn-red px-3 py-2 text-xs cursor-pointer whitespace-nowrap">
                                {uploadingImg[`img-${k}`] ? "Subiendo…" : "Subir"}
                                <input type="file" accept="image/*" className="hidden" disabled={uploadingImg[`img-${k}`]}
                                  onChange={e => { const f = e.target.files[0]; if (!f) return; setUploadingImg(p => ({ ...p, [`img-${k}`]: true })); uploadVehicleImage(f, (url) => setEditing(prev => ({ ...prev, imagen_url: url }))).finally(() => setUploadingImg(p => ({ ...p, [`img-${k}`]: false }))); e.target.value = ""; }}
                                  data-testid="vehicle-image-upload-main" />
                              </label>
                              <button type="button" className="px-3 py-2 text-xs border border-neutral-300 rounded whitespace-nowrap" onClick={() => openPicker("imagen_url")} data-testid="vehicle-image-picker-toggle-main">
                                {pickerOpenFor === "imagen_url" ? "Cerrar" : "Elegir de galería"}
                              </button>
                            </div>
                            {pickerOpenFor === "imagen_url" && (
                              <div className="mt-2 p-2 border border-neutral-200 rounded-lg max-h-56 overflow-y-auto grid grid-cols-4 gap-2" data-testid="vehicle-image-gallery-main">
                                {loadingGallery ? (
                                  <div className="col-span-4 text-xs text-neutral-500 py-4 text-center">Cargando…</div>
                                ) : (cloudinaryImages || []).length === 0 ? (
                                  <div className="col-span-4 text-xs text-neutral-500 py-4 text-center">Aún no hay imágenes subidas</div>
                                ) : cloudinaryImages.map(img => (
                                  <img key={img.public_id} src={img.url} alt="" className="w-full aspect-square object-cover rounded cursor-pointer hover:opacity-70 border border-neutral-200" onClick={() => pickFromGallery("imagen_url", img.url)} />
                                ))}
                              </div>
                            )}
                          </div>
                        ) : k === "imagenes_galeria_csv" ? (
                          <div className="space-y-2">
                            <input className="pc-input" value={editing[k] ?? ""} onChange={e => setEditing({ ...editing, [k]: e.target.value })} placeholder="URLs separadas por coma, o sube más abajo" />
                            <div className="flex gap-2 flex-wrap">
                              <label className="btn-red px-3 py-2 text-xs cursor-pointer inline-block">
                                {uploadingImg[`img-${k}`] ? "Subiendo…" : "+ Añadir imagen a la galería"}
                                <input type="file" accept="image/*" className="hidden" disabled={uploadingImg[`img-${k}`]}
                                  onChange={e => { const f = e.target.files[0]; if (!f) return; setUploadingImg(p => ({ ...p, [`img-${k}`]: true })); uploadVehicleImage(f, (url) => setEditing(prev => ({ ...prev, imagenes_galeria_csv: [prev.imagenes_galeria_csv, url].filter(Boolean).join(", ") }))).finally(() => setUploadingImg(p => ({ ...p, [`img-${k}`]: false }))); e.target.value = ""; }}
                                  data-testid="vehicle-image-upload-gallery" />
                              </label>
                              <button type="button" className="px-3 py-2 text-xs border border-neutral-300 rounded" onClick={() => openPicker("imagenes_galeria_csv")} data-testid="vehicle-image-picker-toggle-gallery">
                                {pickerOpenFor === "imagenes_galeria_csv" ? "Cerrar" : "Elegir de galería"}
                              </button>
                            </div>
                            {pickerOpenFor === "imagenes_galeria_csv" && (
                              <div className="p-2 border border-neutral-200 rounded-lg max-h-56 overflow-y-auto grid grid-cols-4 gap-2" data-testid="vehicle-image-gallery-gallery">
                                {loadingGallery ? (
                                  <div className="col-span-4 text-xs text-neutral-500 py-4 text-center">Cargando…</div>
                                ) : (cloudinaryImages || []).length === 0 ? (
                                  <div className="col-span-4 text-xs text-neutral-500 py-4 text-center">Aún no hay imágenes subidas</div>
                                ) : cloudinaryImages.map(img => (
                                  <img key={img.public_id} src={img.url} alt="" className="w-full aspect-square object-cover rounded cursor-pointer hover:opacity-70 border border-neutral-200" onClick={() => pickFromGallery("imagenes_galeria_csv", img.url)} />
                                ))}
                              </div>
                            )}
                          </div>
                        ) : k === "color_agenda" ? (
                          <div>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {AGENDA_COLORS.map(c => (
                                <button key={c} type="button" onClick={() => setEditing({ ...editing, color_agenda: c })}
                                  className={`w-7 h-7 rounded-full border-2 ${editing[k] === c ? "border-black" : "border-transparent"}`}
                                  style={{ backgroundColor: c }} title={c} data-testid={`agenda-color-${c}`} />
                              ))}
                              <input type="color" className="w-7 h-7 rounded-full border border-neutral-300 cursor-pointer"
                                value={editing[k] || "#ef4444"} onChange={e => setEditing({ ...editing, color_agenda: e.target.value })}
                                title="Color personalizado" data-testid="agenda-color-custom" />
                            </div>
                          </div>
                        ) : k === "precio_dia" ? (
                          <input type="number" min="0" step="0.01" className="pc-input" value={editing[k] ?? ""}
                            onChange={e => {
                              const val = e.target.value;
                              setEditing(prev => {
                                const next = { ...prev, precio_dia: val };
                                if (prev.precio_hora === undefined || prev.precio_hora === "" || prev.precio_hora === null) {
                                  const n = parseFloat(val);
                                  if (Number.isFinite(n)) next.precio_hora = Math.round(n * 0.10 * 100) / 100;
                                }
                                return next;
                              });
                            }} data-testid="vehicle-precio-dia" />
                        ) : k === "precio_hora" ? (
                          <div className="flex gap-2 items-center">
                            <input type="number" min="0" step="0.01" className="pc-input" value={editing[k] ?? ""} onChange={e => setEditing({ ...editing, precio_hora: e.target.value })} data-testid="vehicle-precio-hora" />
                            <button type="button" className="text-xs px-2 py-2 border border-neutral-300 rounded whitespace-nowrap"
                              onClick={() => { const n = parseFloat(editing.precio_dia); if (Number.isFinite(n)) setEditing({ ...editing, precio_hora: Math.round(n * 0.10 * 100) / 100 }); }}
                              data-testid="vehicle-precio-hora-10pct">10%</button>
                          </div>
                        ) : k === "nivel_combustible" ? (
                          <input type="number" min="0" max="100" step="5" className="pc-input" placeholder="0-100" value={editing[k] ?? ""} onChange={e => setEditing({ ...editing, [k]: e.target.value === "" ? "" : Math.min(100, Math.max(0, Number(e.target.value))) })} data-testid="vehicle-nivel-combustible" />
                        ) : k === "kilometros_salida" ? (
                          <input type="number" min="0" className="pc-input" value={editing[k] ?? ""} onChange={e => setEditing({ ...editing, [k]: e.target.value })} data-testid="vehicle-km-salida" />
                        ) : (
                          <input className="pc-input" value={editing[k] ?? ""} onChange={e => setEditing({ ...editing, [k]: e.target.value })} />
                        )}
                      </div>
                    ))}
                  </div>
                </fieldset>
              ))}

              <label className="flex items-center gap-2 text-sm mt-2">
                <input type="checkbox" checked={!!editing.visible} onChange={e => setEditing({ ...editing, visible: e.target.checked })} />
                Visible en la web
              </label>

              {editing.id && (
                <div className="mt-5 pt-5 border-t border-neutral-200">
                  <h4 className="font-display text-lg mb-3">Bloquear días</h4>
                  <div className="flex flex-wrap gap-2 items-end mb-3">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-neutral-500">Desde</label>
                      <input type="date" className="pc-input" value={newBlock.fecha_inicio} onChange={e => setNewBlock({ ...newBlock, fecha_inicio: e.target.value })} data-testid="vehicle-bloqueo-fecha-inicio" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-neutral-500">Hasta</label>
                      <input type="date" className="pc-input" value={newBlock.fecha_fin} onChange={e => setNewBlock({ ...newBlock, fecha_fin: e.target.value })} data-testid="vehicle-bloqueo-fecha-fin" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-neutral-500">Motivo (opcional)</label>
                      <input className="pc-input" value={newBlock.motivo} onChange={e => setNewBlock({ ...newBlock, motivo: e.target.value })} placeholder="Taller, ITV…" data-testid="vehicle-bloqueo-motivo" />
                    </div>
                    <button type="button" onClick={crearBloqueoVehiculo} disabled={savingBlock} className="px-3 py-2 text-xs border border-neutral-300 rounded disabled:opacity-50" data-testid="vehicle-bloqueo-crear-btn">
                      {savingBlock ? "Bloqueando…" : "Bloquear"}
                    </button>
                  </div>
                  {vehicleBlocks.length === 0 ? (
                    <p className="text-xs text-neutral-500">Sin días bloqueados.</p>
                  ) : (
                    <div className="space-y-1">
                      {vehicleBlocks.map(b => (
                        <div key={b.id} className="flex items-center justify-between text-xs bg-neutral-50 border border-neutral-200 rounded px-2 py-1" data-testid={`vehicle-bloqueo-row-${b.id}`}>
                          <span>{(b.fecha_inicio || "").slice(0, 10)} → {(b.fecha_fin || "").slice(0, 10)}{b.motivo && ` · ${b.motivo}`}</span>
                          <button type="button" onClick={() => eliminarBloqueoVehiculo(b.id)} className="text-red-600 hover:underline" data-testid={`vehicle-bloqueo-delete-${b.id}`}>Eliminar</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sticky footer with actions */}
            <div className="px-6 py-4 border-t border-neutral-200 bg-white flex gap-2 justify-end sticky bottom-0" data-testid="vehicle-modal-actions">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-neutral-300 rounded" data-testid="cancel-vehicle-btn">Cancelar</button>
              <button onClick={() => save(editing)} className="btn-red px-6 py-2 text-sm" data-testid="save-vehicle-btn">Guardar cambios</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DIAS_SEMANA = ["L", "M", "X", "J", "V", "S", "D"];
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function CalendarioTab() {
  const [vehicles, setVehicles] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [bloqueos, setBloqueos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get("/api/vehicles?visible_only=false"),
      axios.get("/api/reservations", { headers: getAuthHeaders() }),
      axios.get("/api/admin/bloqueos", { headers: getAuthHeaders() }),
    ]).then(([vRes, rRes, bRes]) => {
      setVehicles(vRes.data || []);
      setReservations((rRes.data || []).filter(r => r.status === "paid" || r.status === "pending"));
      setBloqueos(bRes.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const vehicleById = Object.fromEntries(vehicles.map(v => [v.id, v]));

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = (firstDay.getDay() + 6) % 7; // Lunes = 0
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const dateStr = (day) => `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const reservasDelDia = (day) => {
    const ds = dateStr(day);
    return reservations.filter(r => (r.fecha_recogida || "").slice(0, 10) <= ds && ds <= (r.fecha_devolucion || "").slice(0, 10));
  };

  const bloqueosDelDia = (day) => {
    const ds = dateStr(day);
    return bloqueos.filter(b => (b.fecha_inicio || "").slice(0, 10) <= ds && ds <= (b.fecha_fin || "").slice(0, 10));
  };

  return (
    <div data-testid="calendario-tab">
      <div className="flex items-center justify-between mb-4">
        <button className="px-3 py-1 text-sm border border-neutral-300 rounded" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendario-prev">← Anterior</button>
        <h3 className="font-display text-xl">{MESES[month]} {year}</h3>
        <button className="px-3 py-1 text-sm border border-neutral-300 rounded" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendario-next">Siguiente →</button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500">Cargando…</p>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DIAS_SEMANA.map(d => (
              <div key={d} className="text-xs uppercase tracking-wider text-neutral-500 text-center py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => (
              <div key={idx} className={`min-h-[90px] border border-neutral-200 rounded p-1 ${day ? "bg-white" : "bg-neutral-50"}`} data-testid={day ? `calendario-dia-${dateStr(day)}` : undefined}>
                {day && (
                  <>
                    <div className="text-xs text-neutral-400 mb-1">{day}</div>
                    <div className="space-y-1">
                      {reservasDelDia(day).map(r => {
                        const v = vehicleById[r.vehicle_id];
                        const color = v?.color_agenda || "#78716c";
                        return (
                          <div key={r.id} className="text-[10px] leading-tight text-white rounded px-1 py-0.5 truncate"
                            style={{ backgroundColor: color }} title={`${v?.marca_modelo || r.vehicle_name || ""} · ${v?.matricula || ""}`}>
                            {v?.matricula || r.vehicle_name || "—"}
                          </div>
                        );
                      })}
                      {bloqueosDelDia(day).map(b => {
                        const v = vehicleById[b.vehicle_id];
                        return (
                          <div key={b.id} className="text-[10px] leading-tight text-white rounded px-1 py-0.5 truncate bg-neutral-400"
                            title={`Bloqueado: ${v?.marca_modelo || ""} ${b.motivo ? "· " + b.motivo : ""}`}>
                            🔒 {v?.matricula || "Bloqueado"}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            {vehicles.filter(v => v.matricula).map(v => (
              <div key={v.id} className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: v.color_agenda || "#78716c" }} />
                {v.matricula} — {v.marca_modelo}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function BloqueosTab() {
  const [vehicles, setVehicles] = useState([]);
  const [bloqueos, setBloqueos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ vehicle_id: "", fecha_inicio: "", fecha_fin: "", motivo: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get("/api/vehicles?visible_only=false"),
      axios.get("/api/admin/bloqueos", { headers: getAuthHeaders() }),
    ]).then(([vRes, bRes]) => {
      setVehicles(vRes.data || []);
      setBloqueos(bRes.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const vehicleById = Object.fromEntries(vehicles.map(v => [v.id, v]));

  const crear = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.fecha_inicio || !form.fecha_fin) {
      toast.error("Selecciona vehículo y ambas fechas"); return;
    }
    setSaving(true);
    try {
      await axios.post("/api/admin/bloqueos", {
        vehicle_id: form.vehicle_id,
        fecha_inicio: `${form.fecha_inicio}T00:00`,
        fecha_fin: `${form.fecha_fin}T23:59`,
        motivo: form.motivo,
      }, { headers: getAuthHeaders() });
      toast.success("Días bloqueados");
      setForm({ vehicle_id: form.vehicle_id, fecha_inicio: "", fecha_fin: "", motivo: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el bloqueo");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este bloqueo?")) return;
    await axios.delete(`/api/admin/bloqueos/${id}`, { headers: getAuthHeaders() });
    load();
  };

  return (
    <div data-testid="bloqueos-tab">
      <form onSubmit={crear} className="bg-neutral-50 p-4 rounded border border-neutral-200 grid grid-cols-1 md:grid-cols-5 gap-3 mb-6 items-end">
        <div>
          <label className="text-xs uppercase tracking-widest text-neutral-500">Vehículo</label>
          <select className="pc-input" value={form.vehicle_id} onChange={e => setForm({ ...form, vehicle_id: e.target.value })} data-testid="bloqueo-vehicle-select">
            <option value="">Selecciona…</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.matricula ? `${v.matricula} — ` : ""}{v.marca_modelo}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-neutral-500">Desde</label>
          <input type="date" className="pc-input" value={form.fecha_inicio} onChange={e => setForm({ ...form, fecha_inicio: e.target.value })} data-testid="bloqueo-fecha-inicio" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-neutral-500">Hasta</label>
          <input type="date" className="pc-input" value={form.fecha_fin} onChange={e => setForm({ ...form, fecha_fin: e.target.value })} data-testid="bloqueo-fecha-fin" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-neutral-500">Motivo (opcional)</label>
          <input className="pc-input" value={form.motivo} onChange={e => setForm({ ...form, motivo: e.target.value })} placeholder="Taller, ITV…" data-testid="bloqueo-motivo" />
        </div>
        <button type="submit" disabled={saving} className="btn-red px-4 py-2 text-sm disabled:opacity-50" data-testid="bloqueo-crear-btn">
          {saving ? "Bloqueando…" : "Bloquear días"}
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-neutral-500">Cargando…</p>
      ) : bloqueos.length === 0 ? (
        <p className="text-sm text-neutral-500">No hay días bloqueados.</p>
      ) : (
        <div className="space-y-2">
          {bloqueos.map(b => {
            const v = vehicleById[b.vehicle_id];
            return (
              <div key={b.id} className="bg-neutral-50 p-3 rounded border border-neutral-200 flex items-center justify-between gap-3 text-sm" data-testid={`bloqueo-row-${b.id}`}>
                <div>
                  <strong>{v?.matricula || v?.marca_modelo || "Vehículo eliminado"}</strong>
                  {" · "}{(b.fecha_inicio || "").slice(0, 10)} → {(b.fecha_fin || "").slice(0, 10)}
                  {b.motivo && <span className="text-neutral-500"> · {b.motivo}</span>}
                </div>
                <button onClick={() => eliminar(b.id)} className="text-xs text-red-600 hover:underline" data-testid={`bloqueo-delete-${b.id}`}>Eliminar</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CouponsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    return axios.get("/api/coupons", { headers: getAuthHeaders() })
      .then(r => setItems(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({ codigo: "", tipo: "porcentaje", valor: "", activo: true, fecha_caducidad: "", usos_max: "" });

  const save = async () => {
    if (!editing.codigo || !editing.valor) { toast.error("Código y valor son obligatorios"); return; }
    const data = {
      ...editing,
      valor: parseFloat(editing.valor),
      usos_max: editing.usos_max === "" ? null : parseInt(editing.usos_max),
      fecha_caducidad: editing.fecha_caducidad || null,
    };
    try {
      if (editing.id) await axios.put(`/api/coupons/${editing.id}`, data, { headers: getAuthHeaders() });
      else await axios.post("/api/coupons", data, { headers: getAuthHeaders() });
      toast.success("Cupón guardado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el cupón");
    }
  };

  const toggleActivo = async (c) => {
    try {
      await axios.put(`/api/coupons/${c.id}`, { activo: !c.activo }, { headers: getAuthHeaders() });
      toast.success(!c.activo ? "Cupón habilitado" : "Cupón deshabilitado");
      load();
    } catch (e) {
      toast.error("No se pudo cambiar el estado del cupón");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar cupón?")) return;
    await axios.delete(`/api/coupons/${id}`, { headers: getAuthHeaders() });
    load();
  };

  if (loading) return <div className="text-neutral-500">Cargando…</div>;

  return (
    <div>
      <button onClick={() => setEditing(blank())} className="btn-red px-4 py-2 text-sm mb-4" data-testid="coupon-add-btn">+ Añadir Cupón</button>
      <div className="space-y-2">
        {items.map(c => (
          <div key={c.id} className="bg-neutral-50 p-3 rounded border border-neutral-200 flex items-center justify-between gap-3" data-testid={`coupon-row-${c.codigo}`}>
            <div className="text-sm">
              <div className="font-semibold">{c.codigo} <span className={`ml-2 text-xs px-2 py-0.5 rounded ${c.activo ? "bg-green-100 text-green-700" : "bg-neutral-200 text-neutral-600"}`}>{c.activo ? "Activo" : "Deshabilitado"}</span></div>
              <div className="text-neutral-600">
                {c.tipo === "porcentaje" ? `${c.valor}% de descuento` : `${fmtPrecio(c.valor)} de descuento`}
                {c.fecha_caducidad && ` · caduca ${c.fecha_caducidad}`}
                {c.usos_max ? ` · ${c.usos_actuales || 0}/${c.usos_max} usos` : ` · ${c.usos_actuales || 0} usos`}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => toggleActivo(c)} className="text-xs px-2 py-1 bg-neutral-200 rounded" data-testid={`coupon-toggle-${c.codigo}`}>
                {c.activo ? "Deshabilitar" : "Habilitar"}
              </button>
              <button onClick={() => setEditing({ ...c, valor: String(c.valor), usos_max: c.usos_max ?? "" })} className="text-xs px-2 py-1 bg-neutral-200 rounded">Editar</button>
              <button onClick={() => remove(c.id)} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Eliminar</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-neutral-500">Sin cupones aún.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="font-display text-2xl mb-4">{editing.id ? "Editar" : "Añadir"} Cupón</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-500">Código</label>
                <input className="pc-input uppercase" value={editing.codigo} onChange={e => setEditing({ ...editing, codigo: e.target.value.toUpperCase() })} data-testid="coupon-codigo-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-neutral-500">Tipo</label>
                  <select className="pc-input" value={editing.tipo} onChange={e => setEditing({ ...editing, tipo: e.target.value })}>
                    <option value="porcentaje">Porcentaje (%)</option>
                    <option value="importe">Importe fijo (€)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-neutral-500">Valor</label>
                  <input type="number" min="0" step="0.01" className="pc-input" value={editing.valor} onChange={e => setEditing({ ...editing, valor: e.target.value })} data-testid="coupon-valor-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-neutral-500">Fecha caducidad (opcional)</label>
                  <input type="date" className="pc-input" value={editing.fecha_caducidad || ""} onChange={e => setEditing({ ...editing, fecha_caducidad: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-neutral-500">Usos máximos (opcional)</label>
                  <input type="number" min="1" className="pc-input" value={editing.usos_max} onChange={e => setEditing({ ...editing, usos_max: e.target.value })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.activo} onChange={e => setEditing({ ...editing, activo: e.target.checked })} />
                Activo
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Cancelar</button>
              <button onClick={save} className="btn-red px-4 py-2 text-sm" data-testid="coupon-save-btn">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CrudTab({ name, label, fields, numericFields = [] }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => axios.get(`/api/${name}`).then(r => setItems(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [name]);

  const save = async () => {
    const data = { ...editing };
    numericFields.forEach(k => { if (data[k] !== undefined && data[k] !== "") data[k] = parseInt(data[k]); });
    if (editing.id) await axios.put(`/api/${name}/${editing.id}`, data);
    else await axios.post(`/api/${name}`, data);
    toast.success("Guardado"); setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    await axios.delete(`/api/${name}/${id}`); load();
  };

  return (
    <div>
      <button onClick={() => setEditing({})} className="btn-red px-4 py-2 text-sm mb-4">+ Añadir {label}</button>
      <div className="space-y-2">
        {items.map(it => (
          <div key={it.id} className="bg-neutral-50 p-3 rounded border border-neutral-200 flex items-start justify-between gap-3">
            <div className="text-sm">
              {fields.map(([k, l]) => (
                <div key={k}><strong>{l}:</strong> {String(it[k] ?? "").slice(0, 120)}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(it)} className="text-xs px-2 py-1 bg-neutral-200 rounded">Editar</button>
              <button onClick={() => remove(it.id)} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Eliminar</button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="font-display text-2xl mb-4">{editing.id ? "Editar" : "Añadir"} {label}</h3>
            <div className="space-y-3">
              {fields.map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-wider text-neutral-500">{l}</label>
                  {k === "contenido" || k === "texto" || k === "respuesta" ? (
                    <textarea className="pc-input" rows="4" value={editing[k] || ""} onChange={e => setEditing({ ...editing, [k]: e.target.value })} />
                  ) : (
                    <input className="pc-input" value={editing[k] ?? ""} onChange={e => setEditing({ ...editing, [k]: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Cancelar</button>
              <button onClick={save} className="btn-red px-4 py-2 text-sm">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get("/api/leads-venta", { headers: getAuthHeaders() }).then(r => setItems(r.data || [])); }, []);
  return (
    <div className="space-y-2">
      {items.map(l => (
        <div key={l.id} className="bg-neutral-50 p-3 rounded border border-neutral-200 text-sm">
          <div><strong>{l.nombre}</strong> · {l.email} · {l.telefono}</div>
          <div className="text-neutral-600 text-xs">{l.created_at}</div>
          <div>{l.mensaje}</div>
        </div>
      ))}
      {items.length === 0 && <div className="text-neutral-500">Sin leads aún.</div>}
    </div>
  );
}

const USERS_STORAGE_KEY = "pillauncoche_users";

function UsuariosTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ nombre: "", email: "", telefono: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get("/api/admin/users", { headers: getAuthHeaders() });
      setUsers(r.data || []);
      // Keep a local mirror for offline debugging / legacy callers
      try { localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(r.data || [])); } catch (err) { /* ignore */ }
    } catch (e) {
      toast.error("No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patchUser = async (id, payload) => {
    try {
      const r = await axios.patch(`/api/admin/users/${id}`, payload, { headers: getAuthHeaders() });
      const next = users.map(u => u.id === id ? r.data : u);
      setUsers(next);
      try { localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(next)); } catch (err) { /* ignore */ }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
      return false;
    }
  };

  const toggleBlock = async (u) => {
    const next = u.estado === "Bloqueado" ? "Activo" : "Bloqueado";
    const ok = await patchUser(u.id, { estado: next });
    if (ok) toast.success(next === "Bloqueado" ? "Usuario bloqueado" : "Usuario desbloqueado");
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setDraft({ nombre: u.nombre || "", email: u.email || "", telefono: u.telefono || "" });
  };

  const cancelEdit = () => { setEditingId(null); setDraft({ nombre: "", email: "", telefono: "" }); };

  const saveEdit = async (id) => {
    const ok = await patchUser(id, draft);
    if (ok) { cancelEdit(); toast.success("Usuario actualizado"); }
  };

  const inputCls = "w-full px-2 py-1 text-sm bg-white border border-neutral-300 rounded focus:outline-none focus:border-[var(--pc-red)]";

  return (
    <div data-testid="usuarios-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-black">Gestión de Usuarios</h2>
        <button onClick={load} className="px-3 py-1 text-xs uppercase tracking-wider border border-neutral-400 text-neutral-700 rounded hover:bg-neutral-100" data-testid="users-refresh">Refrescar</button>
      </div>
      <div className="overflow-x-auto border border-neutral-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-black text-white uppercase text-xs tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Teléfono</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const isEditing = editingId === u.id;
              const blocked = u.estado === "Bloqueado";
              const shortId = String(u.id || "").slice(0, 8);
              return (
                <tr key={u.id} className="border-t border-neutral-200 hover:bg-neutral-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 text-neutral-700 font-mono text-xs" title={u.id}>{shortId}</td>
                  <td className="px-3 py-2">
                    {isEditing
                      ? <input className={inputCls} value={draft.nombre} onChange={e => setDraft(d => ({ ...d, nombre: e.target.value }))} data-testid={`user-edit-nombre-${u.id}`} />
                      : u.nombre}
                  </td>
                  <td className="px-3 py-2">
                    {isEditing
                      ? <input className={inputCls} value={draft.email} onChange={e => setDraft(d => ({ ...d, email: e.target.value }))} data-testid={`user-edit-email-${u.id}`} />
                      : u.email}
                  </td>
                  <td className="px-3 py-2">
                    {isEditing
                      ? <input className={inputCls} value={draft.telefono} onChange={e => setDraft(d => ({ ...d, telefono: e.target.value }))} data-testid={`user-edit-telefono-${u.id}`} />
                      : (u.telefono || <span className="text-neutral-400">—</span>)}
                  </td>
                  <td className={`px-3 py-2 font-semibold ${blocked ? "text-[var(--pc-red)]" : "text-neutral-800"}`} data-testid={`user-estado-${u.id}`}>
                    {u.estado || "Activo"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {isEditing ? (
                      <>
                        <button onClick={() => saveEdit(u.id)} className="btn-red px-3 py-1 text-xs uppercase tracking-wider mr-2" data-testid={`user-save-${u.id}`}>Guardar</button>
                        <button onClick={cancelEdit} className="px-3 py-1 text-xs uppercase tracking-wider border border-neutral-400 text-neutral-700 rounded hover:bg-neutral-100" data-testid={`user-cancel-${u.id}`}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(u)} className="px-3 py-1 text-xs uppercase tracking-wider border border-neutral-400 text-neutral-700 rounded hover:bg-neutral-100 mr-2" data-testid={`user-edit-${u.id}`}>Editar</button>
                        <button onClick={() => toggleBlock(u)} className={`px-3 py-1 text-xs uppercase tracking-wider rounded ${blocked ? "bg-black text-white hover:bg-neutral-800" : "btn-red"}`} data-testid={`user-toggle-${u.id}`}>
                          {blocked ? "Desbloquear" : "Bloquear"}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-500">Sin usuarios registrados aún.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-500">Cargando...</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-neutral-500 mt-3">Solo se muestran usuarios con rol <code>customer</code>. Las cuentas de administrador se gestionan aparte por seguridad.</p>
    </div>
  );
}

function UbicacionesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => axios.get("/api/ubicaciones").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const data = { ...editing };
    if (data.latitud !== "" && data.latitud !== undefined) data.latitud = parseFloat(data.latitud);
    if (data.longitud !== "" && data.longitud !== undefined) data.longitud = parseFloat(data.longitud);
    const headers = getAuthHeaders();
    if (editing.id) await axios.put(`/api/ubicaciones/${editing.id}`, data, { headers });
    else await axios.post(`/api/ubicaciones`, data, { headers });
    toast.success("Guardado"); setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar ubicación?")) return;
    await axios.delete(`/api/ubicaciones/${id}`, { headers: getAuthHeaders() }); load();
  };

  return (
    <div data-testid="ubicaciones-tab">
      <button onClick={() => setEditing({ nombre_zona: "", direccion_completa: "", latitud: "", longitud: "" })} className="btn-red px-4 py-2 text-sm mb-4" data-testid="add-ubicacion-btn">+ Añadir punto de recogida</button>
      <div className="space-y-2">
        {items.map(u => (
          <div key={u.id} className="bg-neutral-50 p-3 rounded border border-neutral-200 flex items-start justify-between gap-3" data-testid={`ubicacion-row-${u.id}`}>
            <div className="text-sm">
              <div><strong>{u.nombre_zona}</strong></div>
              <div className="text-neutral-600">{u.direccion_completa}</div>
              <div className="text-xs text-neutral-500">lat: {u.latitud} · lng: {u.longitud}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(u)} className="text-xs px-2 py-1 bg-neutral-200 rounded">Editar</button>
              <button onClick={() => remove(u.id)} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Eliminar</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-neutral-500">Sin ubicaciones todavía.</div>}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="font-display text-2xl mb-4">{editing.id ? "Editar" : "Añadir"} punto de recogida</h3>
            <div className="space-y-3">
              {[["nombre_zona", "Nombre de la zona"], ["direccion_completa", "Dirección completa"], ["latitud", "Latitud"], ["longitud", "Longitud"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-wider text-neutral-500">{l}</label>
                  <input className="pc-input" value={editing[k] ?? ""} onChange={e => setEditing({ ...editing, [k]: e.target.value })} data-testid={`ubicacion-input-${k}`} />
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Cancelar</button>
              <button onClick={save} className="btn-red px-4 py-2 text-sm" data-testid="ubicacion-save-btn">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NavConfigTab() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState({}); // {id: number}
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const { data } = await axios.get("/api/nav-config");
    const list = data || [];
    setItems(list);
    const next = {};
    list.forEach((it, i) => { next[it.id] = it.orden ?? i; });
    setOrders(next);
  };
  useEffect(() => { load(); }, []);
  const toggle = async (it) => {
    await axios.put(`/api/nav-config/${it.id}`, { activo: !it.activo }, { headers: getAuthHeaders() });
    load();
  };
  const setOrden = (id, val) => setOrders(o => ({ ...o, [id]: val }));
  const saveOrder = async () => {
    setSaving(true);
    try {
      const payload = { items: items.map(it => ({ id: it.id, orden: Number(orders[it.id] ?? 0) })) };
      await axios.put("/api/nav-config-order", payload, { headers: getAuthHeaders() });
      toast.success("Orden guardado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando el orden");
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="navconfig-tab">
      <p className="text-sm text-neutral-600 mb-4">Activa o desactiva secciones del menú superior, y asigna un número de orden (menor = izquierda). Los cambios se aplican inmediatamente al navbar público.</p>
      <table className="w-full text-sm border border-neutral-200 rounded overflow-hidden">
        <thead className="bg-neutral-100">
          <tr>
            <th className="px-3 py-2 text-left">Sección</th>
            <th className="px-3 py-2 text-left w-32">Orden</th>
            <th className="px-3 py-2 text-left w-32">Visibilidad</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr key={it.id} className="border-t border-neutral-200" data-testid={`nav-row-${it.id}`}>
              <td className="px-3 py-2 font-semibold">{it.nombre_seccion}</td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  min={0}
                  value={orders[it.id] ?? 0}
                  onChange={e => setOrden(it.id, e.target.value)}
                  className="pc-input py-1 text-sm w-20"
                  data-testid={`nav-order-${it.id}`}
                />
              </td>
              <td className="px-3 py-2">
                <button onClick={() => toggle(it)} className={`text-xs px-3 py-1 rounded ${it.activo ? "bg-green-100 text-green-700" : "bg-neutral-200 text-neutral-600"}`} data-testid={`nav-toggle-${it.id}`}>
                  {it.activo ? "Visible" : "Oculto"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={saveOrder} disabled={saving} className="btn-red mt-4 px-6 py-2 text-sm" data-testid="nav-save-order">
        {saving ? "Guardando…" : "Guardar Orden"}
      </button>
    </div>
  );
}


function CategoriasTab() {
  const [items, setItems] = useState([]);
  const [nombre, setNombre] = useState("");
  const load = () => axios.get("/api/categorias").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) return;
    try {
      await axios.post("/api/categorias", { nombre: nombre.trim(), visible: true }, { headers: getAuthHeaders() });
      setNombre(""); load(); toast.success("Categoría añadida");
    } catch { toast.error("Error al crear"); }
  };
  const toggle = async (c) => {
    await axios.put(`/api/categorias/${c.id}`, { visible: !c.visible }, { headers: getAuthHeaders() }); load();
  };
  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar categoría "${c.nombre}"?`)) return;
    try {
      await axios.delete(`/api/categorias/${c.id}`, { headers: getAuthHeaders() });
      toast.success("Eliminada"); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };
  return (
    <div data-testid="categorias-tab">
      <form onSubmit={add} className="flex gap-2 mb-4">
        <input className="pc-input flex-1" placeholder="Nueva categoría" value={nombre} onChange={e => setNombre(e.target.value)} data-testid="cat-input" />
        <button type="submit" className="btn-red px-4 py-2 text-sm" data-testid="cat-add-btn">Añadir</button>
      </form>
      <div className="space-y-2">
        {items.map(c => (
          <div key={c.id} className="bg-neutral-50 p-3 rounded border border-neutral-200 flex items-center justify-between" data-testid={`cat-row-${c.id}`}>
            <div className="font-semibold">{c.nombre}</div>
            <div className="flex gap-2">
              <button onClick={() => toggle(c)} className={`text-xs px-3 py-1 rounded ${c.visible ? "bg-green-100 text-green-700" : "bg-neutral-200 text-neutral-600"}`}>{c.visible ? "Visible" : "Oculto"}</button>
              <button onClick={() => remove(c)} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Eliminar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Pilla3Tab() {
  const [cfg, setCfg] = useState(null);
  const load = () => axios.get("/api/pilla3-config").then(r => setCfg(r.data));
  useEffect(() => { load(); }, []);
  if (!cfg) return <div>Cargando…</div>;

  const setField = (k, v) => setCfg({ ...cfg, [k]: v });
  const setPlan = (i, k, v) => {
    const planes = [...(cfg.planes || [])];
    planes[i] = { ...planes[i], [k]: k === "nombre" ? v : Number(v) };
    setCfg({ ...cfg, planes });
  };
  const addPlan = () => setCfg({ ...cfg, planes: [...(cfg.planes || []), { nombre: "Nuevo", precio_mes: 0, km_totales: 0, coste_total: 0 }] });
  const delPlan = (i) => setCfg({ ...cfg, planes: cfg.planes.filter((_, j) => j !== i) });

  const save = async () => {
    try {
      await axios.put("/api/pilla3-config", cfg, { headers: getAuthHeaders() });
      toast.success("Pilla3 guardado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    }
  };

  return (
    <div data-testid="pilla3-tab" className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase text-neutral-500">Título</label>
          <input className="pc-input" value={cfg.titulo || ""} onChange={e => setField("titulo", e.target.value)} data-testid="pilla3-titulo" />
        </div>
        <div>
          <label className="text-xs uppercase text-neutral-500">Subtítulo</label>
          <input className="pc-input" value={cfg.subtitulo || ""} onChange={e => setField("subtitulo", e.target.value)} data-testid="pilla3-subtitulo" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase text-neutral-500">Descripción</label>
          <textarea className="pc-input" rows={4} value={cfg.descripcion || ""} onChange={e => setField("descripcion", e.target.value)} data-testid="pilla3-descripcion" />
        </div>
        <div>
          <label className="text-xs uppercase text-neutral-500">Duración (label)</label>
          <input className="pc-input" value={cfg.duracion_label || ""} onChange={e => setField("duracion_label", e.target.value)} />
        </div>
        <div>
          <label className="text-xs uppercase text-neutral-500">Incluye (label)</label>
          <input className="pc-input" value={cfg.incluye_label || ""} onChange={e => setField("incluye_label", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase text-neutral-500">Título tabla tarifas</label>
          <input className="pc-input" value={cfg.tabla_titulo || ""} onChange={e => setField("tabla_titulo", e.target.value)} />
        </div>
        <div>
          <label className="text-xs uppercase text-neutral-500">Imagen URL</label>
          <input className="pc-input" value={cfg.imagen_url || ""} onChange={e => setField("imagen_url", e.target.value)} />
        </div>
        <div>
          <label className="text-xs uppercase text-neutral-500">WhatsApp URL</label>
          <input className="pc-input" value={cfg.whatsapp_url || ""} onChange={e => setField("whatsapp_url", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase text-neutral-500">Ventaja comercial</label>
          <textarea className="pc-input" rows={2} value={cfg.ventaja_comercial || ""} onChange={e => setField("ventaja_comercial", e.target.value)} />
        </div>
      </div>

      <div className="border border-neutral-200 rounded p-3">
        <div className="flex justify-between items-center mb-2">
          <strong>Planes</strong>
          <button onClick={addPlan} className="text-xs px-3 py-1 bg-neutral-200 rounded" data-testid="pilla3-add-plan">+ Añadir plan</button>
        </div>
        {(cfg.planes || []).map((p, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2 items-end" data-testid={`pilla3-plan-${i}`}>
            <div><label className="text-xs text-neutral-500">Nombre</label><input className="pc-input" value={p.nombre} onChange={e => setPlan(i, "nombre", e.target.value)} /></div>
            <div><label className="text-xs text-neutral-500">€/mes</label><input type="number" step="0.01" className="pc-input" value={p.precio_mes} onChange={e => setPlan(i, "precio_mes", e.target.value)} /></div>
            <div><label className="text-xs text-neutral-500">Km totales</label><input type="number" className="pc-input" value={p.km_totales} onChange={e => setPlan(i, "km_totales", e.target.value)} /></div>
            <div><label className="text-xs text-neutral-500">Coste total</label><input type="number" step="0.01" className="pc-input" value={p.coste_total} onChange={e => setPlan(i, "coste_total", e.target.value)} /></div>
            <button onClick={() => delPlan(i)} className="text-xs px-3 py-2 bg-red-100 text-red-700 rounded">Eliminar</button>
          </div>
        ))}
      </div>

      <button onClick={save} className="btn-red px-6 py-3" data-testid="pilla3-save">Guardar Pilla3</button>
    </div>
  );
}


function ConfigTab() {
  const [plazo, setPlazo] = useState(48);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get("/api/invoices/config").then(r => setPlazo(r.data.plazo_horas || 48)).catch(() => { });
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put("/api/invoices/config", { plazo_horas: Number(plazo) }, { headers: getAuthHeaders() });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl space-y-6" data-testid="config-tab">
      <h3 className="font-display text-2xl mb-2">Configuración global</h3>

      <div className="border border-neutral-200 rounded-lg p-5">
        <label className="text-xs uppercase tracking-widest text-neutral-500">Plazo de envío automático de facturas (en horas)</label>
        <input
          type="number"
          min={1}
          max={720}
          value={plazo}
          onChange={e => setPlazo(e.target.value)}
          className="pc-input mt-1"
          data-testid="config-plazo-horas"
        />
        <p className="text-xs text-neutral-500 mt-2">
          Si no envías la factura manualmente, se generará automáticamente pasadas estas horas desde la fecha de devolución del coche.
          Valor recomendado: 48 horas.
        </p>
        <button onClick={save} disabled={saving} className="btn-red mt-4 px-6 py-2 text-sm" data-testid="config-save">
          {saving ? "..." : "Guardar configuración"}
        </button>
      </div>

      <MaintenanceToggle />
      <VerificationToggles />
      <SystemConfigForm />
    </div>
  );
}

const SYSTEM_KEYS = [
  { key: "GOOGLE_CLIENT_ID", label: "Google Client ID", type: "password" },
  { key: "GOOGLE_CLIENT_SECRET", label: "Google Client Secret", type: "password" },
  { key: "RESEND_API_KEY", label: "Resend API Key", type: "password" },
  { key: "STRIPE_PUBLIC_KEY", label: "Stripe Public Key (pk_...)", type: "password" },
  { key: "STRIPE_SECRET_KEY", label: "Stripe Secret Key (sk_...)", type: "password" },
  { key: "PAYPAL_CLIENT_ID", label: "PayPal Client ID", type: "password" },
  { key: "DATABASE_URL", label: "URI de Conexión a Base de Datos (DATABASE_URL)", type: "password", help: "Cadena de conexión a MongoDB. Al guardar, el servidor se reconecta en caliente al nuevo clúster y siembra automáticamente si la base está vacía." },
  { key: "RESERVATION_GAP_HOURS", label: "Margen de seguridad entre alquileres (RESERVATION_GAP_HOURS)", type: "number", min: 0, max: 72, help: "Horas mínimas entre la devolución de un cliente y la entrega al siguiente. Se aplica en tiempo real al buscador de vehículos y al calendario." },
  { key: "PRECIO_KM_EXTRA", label: "Precio del km extra (€/km)", type: "number", min: 0.01, max: 100, step: 0.005, help: "Se aplica al checkout (pack de 100 km = precio × 100), a los Términos y al contrato PDF. Actual recomendado: 0.175" },
  { key: "PRECIO_COMBUSTIBLE_LITRO", label: "Precio del combustible faltante (€/litro)", type: "number", min: 0.01, max: 100, step: 0.05, help: "Se muestra en los Términos y en el contrato PDF. Se suma el cargo de servicio indicado en las condiciones." },
  { key: "RENTAL_MIN_HOURS", label: "Alquiler mínimo en horas (RENTAL_MIN_HOURS)", type: "number", min: 1, max: 720, help: "Duración mínima permitida de un alquiler. Si el cliente elige un rango más corto, no puede reservar. Se valida en el checkout y en el servidor." },
];
function MaintenanceToggle() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get("/api/config")
      .then(r => setEnabled(!!r.data?.maintenance_mode))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const next = !enabled;
      await axios.post("/api/admin/maintenance", { enabled: next }, { headers: getAuthHeaders() });
      setEnabled(next);
      toast.success(next ? "Modo mantenimiento ACTIVADO" : "Modo mantenimiento desactivado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar el modo mantenimiento");
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="border border-neutral-200 rounded-lg p-5 mb-4" data-testid="maintenance-toggle-form">
      <h4 className="font-display text-xl mb-1">Modo Mantenimiento</h4>
      <p className="text-xs text-neutral-500 mb-4">
        Al activarlo, todo el sitio muestra una página de "En mantenimiento" excepto este panel de administración.
      </p>
      <button
        onClick={toggle}
        disabled={saving}
        className={`px-4 py-2 text-sm rounded ${enabled ? "bg-red-600 text-white hover:bg-red-700" : "bg-neutral-200 hover:bg-neutral-300"}`}
        data-testid="maintenance-toggle-btn"
      >
        {saving ? "Aplicando…" : enabled ? "Desactivar mantenimiento" : "Activar mantenimiento"}
      </button>
    </div>
  );
}

function VerificationToggles() {
  const [values, setValues] = useState({ ID_VERIFICATION_ENABLED: false, PHONE_VERIFICATION_ENABLED: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    axios.get("/api/system/config", { headers: getAuthHeaders() })
      .then(r => setValues({
        ID_VERIFICATION_ENABLED: r.data?.ID_VERIFICATION_ENABLED === "true",
        PHONE_VERIFICATION_ENABLED: r.data?.PHONE_VERIFICATION_ENABLED === "true",
      }))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (key, label) => {
    const next = !values[key];
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await axios.put("/api/system/config", { [key]: next ? "true" : "false" }, { headers: getAuthHeaders() });
      setValues(v => ({ ...v, [key]: next }));
      toast.success(`${label} ${next ? "ACTIVADA" : "desactivada"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Error al cambiar ${label}`);
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  if (loading) return null;

  const rows = [
    { key: "ID_VERIFICATION_ENABLED", label: "Verificación de DNI/Carnet (Didit)", desc: "Al activarla, tras el checkout se crea una sesión de verificación Didit y se envía al cliente a completar el escaneo de su documento." },
    { key: "PHONE_VERIFICATION_ENABLED", label: "Verificación de teléfono por SMS (Didit)", desc: "Al activarla, el cliente debe confirmar su móvil mediante un código OTP enviado por SMS antes de continuar." },
  ];

  return (
    <div className="border border-neutral-200 rounded-lg p-5 mb-4" data-testid="verification-toggles-form">
      <h4 className="font-display text-xl mb-1">Verificaciones (Didit)</h4>
      <p className="text-xs text-neutral-500 mb-4">
        Requieren <code>DIDIT_API_KEY</code> configurada (y <code>DIDIT_WORKFLOW_ID</code> / <code>DIDIT_WEBHOOK_SECRET</code> para la de DNI). Si se activan sin esas variables, el checkout seguirá funcionando pero la verificación fallará de forma silenciosa (DNI) o dará error 500 (teléfono).
      </p>
      {rows.map(row => (
        <div key={row.key} className="flex items-start justify-between gap-4 py-3 border-t border-neutral-100 first:border-t-0 first:pt-0">
          <div>
            <div className="text-sm font-medium">{row.label}</div>
            <div className="text-xs text-neutral-500">{row.desc}</div>
          </div>
          <button
            onClick={() => toggle(row.key, row.label)}
            disabled={saving[row.key]}
            className={`shrink-0 px-4 py-2 text-sm rounded ${values[row.key] ? "bg-red-600 text-white hover:bg-red-700" : "bg-neutral-200 hover:bg-neutral-300"}`}
            data-testid={`toggle-${row.key}`}
          >
            {saving[row.key] ? "Aplicando…" : values[row.key] ? "Desactivar" : "Activar"}
          </button>
        </div>
      ))}
    </div>
  );
}

function SystemConfigForm() {
  const [values, setValues] = useState({});
  const [visible, setVisible] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => axios.get("/api/system/config", { headers: getAuthHeaders() })
    .then(r => setValues(r.data || {}))
    .catch(() => toast.error("Error cargando configuración"));

  useEffect(() => { load(); }, []);

  const toggle = (k) => setVisible(v => ({ ...v, [k]: !v[k] }));
  const setVal = (k, val) => setValues(v => ({ ...v, [k]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      SYSTEM_KEYS.forEach(({ key }) => { payload[key] = values[key] ?? ""; });
      await axios.put("/api/system/config", payload, { headers: getAuthHeaders() });
      toast.success("Credenciales actualizadas y recargadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-neutral-200 rounded-lg p-5" data-testid="system-config-form">
      <h4 className="font-display text-xl mb-1">Configuración de APIs y Variables de Entorno</h4>
      <p className="text-xs text-neutral-500 mb-4">
        Al guardar, las nuevas credenciales se aplican <strong>inmediatamente</strong> a Resend, Stripe y Google — sin reiniciar servidor.
      </p>
      <div className="space-y-3">
        {SYSTEM_KEYS.map(({ key, label, type = "password", min, max, step, help }) => (
          <div key={key}>
            <label className="text-xs uppercase tracking-widest text-neutral-500 block mb-1">{label}</label>
            {type === "number" ? (
              <input
                type="number"
                min={min}
                max={max}
                step={step}
                value={values[key] ?? ""}
                onChange={e => setVal(key, e.target.value)}
                className="pc-input font-mono text-sm"
                data-testid={`syscfg-${key.toLowerCase()}`}
              />
            ) : (
              <div className="relative">
                <input
                  type={visible[key] ? "text" : "password"}
                  autoComplete="off"
                  spellCheck={false}
                  value={values[key] ?? ""}
                  onChange={e => setVal(key, e.target.value)}
                  className="pc-input pr-10 font-mono text-sm"
                  data-testid={`syscfg-${key.toLowerCase()}`}
                />
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-black p-1 text-xs"
                  aria-label={visible[key] ? "Ocultar" : "Mostrar"}
                  data-testid={`syscfg-toggle-${key.toLowerCase()}`}
                >
                  {visible[key] ? "🙈" : "👁"}
                </button>
              </div>
            )}
            {help && <p className="text-[11px] text-neutral-500 mt-1">{help}</p>}
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="btn-red mt-5 px-6 py-2 text-sm"
        data-testid="syscfg-save"
      >
        {saving ? "Guardando…" : "Guardar Cambios de Configuración"}
      </button>
    </div>
  );
}

